import React, { useEffect, useState } from 'react';
import LoadManagementHeader from '../Header/LoadManagementHeader';
import { LoadFilters } from '../Filter/LoadFilters';
import LoadList from '../TableList/LoadList';
import { GET_FILTERED_LOAD_MANAGEMENTS, GET_LOADMANAGEMENT_LOADLIST_CSV, GET_SAND_TICKET_EXPORT_STATUS } from '../../graphql/client/queries';
import {
    GetFilteredLoadManagements,
    GetFilteredLoadManagements_getFilteredLoadManagements as Load,
    GetLoadManagementLoadListCSV,
    GetFilteredLoadListInput,
    SortOrder,
} from '../../types/schemaTypes';
import { Error, NoResult } from '../_common';
import { useStoreContext } from '../App/StoreProvider';
import { useMutation, useQuery } from '@apollo/client/react';
import './LoadManagement.scss';
import { LoadStatusEnum } from '../../types/LoadStatusEnum';

import * as LoadManagementListActions from '../../store/actions/loadManagementList';
import { useAuthContext } from '../Auth/AuthProvider';
import { PermissionEnum } from '../../types/PermissionEnum';
import ForbiddenError from '../_common/Error/ForbiddenError';
import LoadManagementLoadData from './LoadManagementLoadData';
import { Base64 } from 'js-base64';
import { CREATE_SAND_TICKET_EXPORT } from '../../graphql/client/mutations';
// Do not change this value unless the associate backend value has been updated - (8/22/19) BS
const PAGE_SIZE = 50;
const EXPORT_STATUS_POLL_INTERVAL = 10000;

interface SelectedLoad {
    loadId: number;
    loadNumber: number;
}

interface CreateSandTicketExportData {
    createSandTicketExport: {
        exportId: string;
        status: string;
    } | null;
}

interface CreateSandTicketExportVariables {
    input?: GetFilteredLoadListInput | null;
}

interface SandTicketExportStatusData {
    getSandTicketExportStatus: {
        status: string;
        totalImages: number;
        processedImages: number;
        downloadUrl?: string | null;
        error?: string | null;
    } | null;
}

interface SandTicketExportStatusVariables {
    exportId: string;
}

interface TicketExportGraphQLError {
    extensions?: {
        response?: {
            body?: {
                Message?: string;
            };
        };
    };
}

interface TicketExportError {
    message?: string;
    errors?: TicketExportGraphQLError[];
    graphQLErrors?: TicketExportGraphQLError[];
    extensions?: TicketExportGraphQLError['extensions'];
    bodyText?: string;
    body?: unknown;
    result?: unknown;
    response?: unknown;
    networkError?: unknown;
    cause?: unknown;
}

const findTicketExportApiMessage = (value: unknown, seen = new Set<object>()): string | undefined => {
    if (typeof value === 'string') {
        try {
            return findTicketExportApiMessage(JSON.parse(value), seen);
        } catch {
            return undefined;
        }
    }
    if (!value || typeof value !== 'object' || seen.has(value)) return undefined;
    seen.add(value);

    if (Array.isArray(value)) {
        return value.map(item => findTicketExportApiMessage(item, seen)).find(Boolean);
    }

    const record = value as Record<string, unknown>;
    const extensions = record.extensions as TicketExportGraphQLError['extensions'];
    const response = (extensions?.response ?? record.response) as { body?: unknown } | undefined;
    const body = (response?.body ?? record.body) as { Message?: unknown; message?: unknown } | undefined;
    const apiMessage = body?.Message ?? body?.message ?? record.Message;
    if (typeof apiMessage === 'string' && apiMessage.trim()) return apiMessage;

    const nestedError = [
        record.errors,
        record.graphQLErrors,
        record.extensions,
        record.response,
        record.body,
        record.bodyText,
        record.result,
        record.networkError,
        record.cause,
    ];
    return nestedError.map(item => findTicketExportApiMessage(item, seen)).find(Boolean)
        ?? (typeof record.message === 'string' && record.message.trim() ? record.message : undefined);
};

const getTicketExportErrorMessage = (error: unknown): string => {
    const fallback = 'Ticket export failed. Please try again.';
    if (typeof error === 'string') {
        return error.replace(/^(?:Internal Server Error:\s*)+/i, '').trim() || fallback;
    }
    if (!error || typeof error !== 'object') return fallback;

    const ticketError = error as TicketExportError;
    return getTicketExportErrorMessage(findTicketExportApiMessage(ticketError) ?? ticketError.message ?? fallback);
};

const LoadManagement = ({ match }: any) => {
    const [state, dispatch] = useStoreContext();
    const {
        loadManagementList: { filters, searchPhrase },
        carrierList: { carrierId },
        loadList: {
            filters: { fromDate, toDate },
        },
    } = state;
    const abortController =new AbortController();
    const loadIds: any = [];
    useEffect(() => {
        dispatch(LoadManagementListActions.resetFilterState());
    }, []);
    const initialArray: SelectedLoad[] = [];
    // TODO: Move local state to jobListReducer
    const [selectedLoads, setSelectedLoads] = useState(initialArray);

    const [sortOrder, setSortOrder] = useState<SortOrder | null>(null);
    const [isSearchOnLoadId, setIsSearchOnLoadId] = useState(true);
    const [csvCalled, setCsvCalled] = useState(false);
    const [ticketsLoading, setTicketsLoading] = useState(false);
    const [activeExportId, setActiveExportId] = useState<string | null>(null);
    const [ticketExportElapsedSeconds, setTicketExportElapsedSeconds] = useState(0);
    const [ticketExportStatus, setTicketExportStatus] = useState<string | null>(null);
    const [ticketExportProgress, setTicketExportProgress] = useState<{ processedImages: number; totalImages: number } | null>(null);
    const [ticketDownloadUrl, setTicketDownloadUrl] = useState<string | null>(null);
    const [ticketExportError, setTicketExportError] = useState<string | null>(null);
    const [createSandTicketExport] = useMutation<CreateSandTicketExportData, CreateSandTicketExportVariables>(CREATE_SAND_TICKET_EXPORT);
    const { data: exportStatus, loading: exportStatusLoading, stopPolling, error: exportStatusError } = useQuery<SandTicketExportStatusData, SandTicketExportStatusVariables>(GET_SAND_TICKET_EXPORT_STATUS, {
        variables: { exportId: activeExportId ?? '' },
        skip: !activeExportId,
        // Apollo 4 polling must be configured on the hook, not query execution options.
        pollInterval: activeExportId ? EXPORT_STATUS_POLL_INTERVAL : 0,
        fetchPolicy: 'network-only',
    });
    const isAdmin = useAuthContext().userHasPermission(PermissionEnum.Admin);
    const isCarrier = useAuthContext().userHasPermission(PermissionEnum.Carrier);
    const isOperationUser = useAuthContext().userHasPermission(PermissionEnum.Operation);

    useEffect(() => {
        if (!ticketsLoading) return;
        const timer = window.setInterval(() => {
            setTicketExportElapsedSeconds(elapsedSeconds => elapsedSeconds + 1);
        }, 1000);
        return () => window.clearInterval(timer);
    }, [ticketsLoading]);

    const variables =  React.useMemo(()=>{
        
        return {
        input: {
            regionIds: filters.regionIds,
            districtIds: filters.districtIds,
            customerIds: filters.customerIds,
            jobStatuses: filters.jobStatuses,
            proppants: filters.proppantIds,
            statuses: filters.statuses,
            carriers: filters.carrierIds,
            drivers: filters.driverIds,
            lofas: filters.lofaIds,
            jobIds: filters.jobIds,
            search: searchPhrase,
            sortOrder: sortOrder,
            atDestinationOutFrom: filters.fromDate,
            atDestinationOutTo: filters.toDate,
            driverCompanyId: carrierId ? carrierId : parseInt(localStorage.getItem('carrierId') || '0'),
            isSearchOnLoadId: searchPhrase ? isSearchOnLoadId : true,
        },
    }},[filters.regionIds,filters.districtIds,filters.customerIds,filters.jobStatuses,filters.statuses,filters.carrierIds,filters.driverIds,filters.lofaIds,filters.jobIds,searchPhrase,sortOrder,filters.fromDate,filters.toDate,carrierId,isSearchOnLoadId]);

    const context = {
       
        fetchOptions: {
            signal: abortController.signal,
        },
    
     }



    const abortRequest = (): void => {
        abortController.abort();
    };


    const downloadCsv = (csvExport): Blob => {
        const result = csvExport.getLoadManagementLoadListCSV.csv;
        const decodedCsv = Base64.decode(result);
        const file = new Blob([decodedCsv], { type: 'application/csv' });
        return file;
    };

    const getCurrentDate = (): string => {
        const today = new Date();
        const day = today.getDate();
        const month = today.getMonth() + 1;
        const year = today.getFullYear();
        const strDay = day < 10 ? `0${day}` : `${day}`;
        const strMonth = month < 10 ? `0${month}` : `${month}`;
        return `${year}${strMonth}${strDay}`;
    };

    const downloadTickets = async (): Promise<void> => {
        setTicketsLoading(true);
        setTicketExportElapsedSeconds(0);
        setTicketExportStatus('Queued');
        setTicketExportProgress(null);
        setTicketDownloadUrl(null);
        setTicketExportError(null);
        try {
            const result = await createSandTicketExport({
                variables: { input: variables.input },
                errorPolicy: 'all',
            });
            if (result.error) {
                throw result.error;
            }
            const data = result.data;
            const exportId = data?.createSandTicketExport?.exportId;
            if (exportId) {
                setActiveExportId(exportId);
            } else {
                setTicketsLoading(false);
                setTicketExportError('Ticket export could not be started. Please try again.');
            }
        } catch (error) {
            setTicketsLoading(false);
            setTicketExportError(getTicketExportErrorMessage(error));
        }
    };

    useEffect(() => {
        if (!activeExportId || exportStatusLoading) return;
        const status = exportStatus?.getSandTicketExportStatus;
        if (exportStatusError) {
            stopPolling();
            setActiveExportId(null);
            setTicketsLoading(false);
            setTicketExportError(getTicketExportErrorMessage(exportStatusError));
            return;
        }
        if (!status) {
            stopPolling();
            setActiveExportId(null);
            setTicketsLoading(false);
            setTicketExportError('Unable to retrieve the ticket export status. Please try again.');
            return;
        }
        setTicketExportStatus(status.status);
        setTicketExportProgress({
            processedImages: status.processedImages,
            totalImages: status.totalImages,
        });
        if (status.status === 'Completed') {
            stopPolling();
            setActiveExportId(null);
            setTicketsLoading(false);
            if (status.totalImages === 0) {
                setTicketExportStatus('No images found');
                setTicketExportError('No sand ticket images found for the selected filters.');
            } else if (status.downloadUrl) {
                setTicketDownloadUrl(status.downloadUrl);
                if (status.error) setTicketExportError(getTicketExportErrorMessage(status.error));
            } else {
                setTicketExportError('Ticket export completed but no download link was returned.');
            }
        } else if (status.status === 'Failed') {
            stopPolling();
            setActiveExportId(null);
            setTicketsLoading(false);
            setTicketExportError(getTicketExportErrorMessage(status.error || 'Ticket export failed. Please try again.'));
        }
    }, [activeExportId, exportStatus, exportStatusLoading, exportStatusError, stopPolling]);

    const { loading: csvLoading, data: csvExport } = useQuery<GetLoadManagementLoadListCSV>(GET_LOADMANAGEMENT_LOADLIST_CSV, {
        variables: {
            input: {
                regionIds: filters.regionIds,
                districtIds: filters.districtIds,
                customerIds: filters.customerIds,
                jobStatuses: filters.jobStatuses,
                proppants: filters.proppantIds,
                statuses: filters.statuses,
                carriers: filters.carrierIds,
                drivers: filters.driverIds,
                lofas: filters.lofaIds,
                jobIds: filters.jobIds,
                search: searchPhrase,
                sortOrder: sortOrder,
                atDestinationOutFrom: filters.fromDate,
                atDestinationOutTo: filters.toDate,
                driverCompanyId: carrierId ? carrierId : parseInt(localStorage.getItem('carrierId') || '0'),
                isSearchOnLoadId: searchPhrase ? isSearchOnLoadId : true,
            },
        },
        skip: !csvCalled,
        fetchPolicy: 'no-cache',
    });

    useEffect(() => {
        if (!csvCalled || csvLoading) return;
        setCsvCalled(false);
        if (csvExport?.getLoadManagementLoadListCSV) {
            const file: Blob = downloadCsv(csvExport);
            const url = window.URL.createObjectURL(file);
            const a = document.createElement('a');
            a.href = url;
            a.download = `List of Loads_${getCurrentDate()}.csv`;
            a.click();
        }
    }, [csvLoading, csvCalled, csvExport]);

    const onCheckAll = (checkedStatus: boolean, loadsList: Load[]): void => {
        const result: SelectedLoad[] = [];
        if (checkedStatus === true) {
            loadsList.forEach((el: Load): void => {
                result.push({ loadId: el.loadId, loadNumber: el.loadNumber });
            });
        }
        setSelectedLoads(result);
    };

    const toggleSortOrder = (): void => {
        if (sortOrder === null) {
            setSortOrder(SortOrder.ASCENDING);
        } else if (sortOrder === SortOrder.ASCENDING) {
            setSortOrder(SortOrder.DESCENDING);
        } else {
            setSortOrder(null);
        }
    };

    document.title = 'Load Management';

    if (!isAdmin && !isOperationUser && !isCarrier) {
        return <ForbiddenError />;
    }

    return (
        <div className="JobLoadsManagement-Container">
            <LoadManagementHeader
                onCheckAll={checked => onCheckAll(checked, [])}
                sortOrder={sortOrder}
                toggleSortOrder={toggleSortOrder}
                selectedJobs={[]}
                isSearchOnLoadId={isSearchOnLoadId}
                setIsSearchOnLoadId={setIsSearchOnLoadId}
            />

            <div className="JobLoadsManagement-BodyContainer">
                <div id="loadsFilterContainer" className="JobLoadsManagement-FiltersContainer">
                    
                    <LoadFilters
                        abortRequest={abortRequest}
                        onFilterToggle={() => undefined}
                        isSearchOnLoadId={isSearchOnLoadId}
                        setIsSearchOnLoadId={setIsSearchOnLoadId}
                        exportCsv={() => {
                            setCsvCalled(true);
                        }}
                        exportTickets={downloadTickets}
                        csvLoading={csvLoading}
                        ticketsLoading={ticketsLoading}
                        ticketExportElapsedSeconds={ticketExportElapsedSeconds}
                        ticketExportStatus={ticketExportStatus}
                        ticketExportProgress={ticketExportProgress}
                        ticketDownloadUrl={ticketDownloadUrl}
                        ticketExportError={ticketExportError}
                    />
                </div>

                {variables.input.driverCompanyId != 0 && (
                    <div
                        id="loadsListContainer"
                        className="JobLoadsManagement-ListContainer"
                    >
                        <LoadManagementLoadData></LoadManagementLoadData>
                    </div>
                )}
                {variables.input.driverCompanyId == 0 && (
                    <div id="loadsListContainer" className="JobLoadsManagement-ListContainer">
                        <NoResult isModal={false} />
                    </div>
                )}
            </div>
        </div>
    );
};

export default LoadManagement;
